import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type EmploymentPeriod = Tables<"employment_periods">;
export type EmploymentPeriodInsert = TablesInsert<"employment_periods">;
export type EmploymentPeriodUpdate = TablesUpdate<"employment_periods">;

type ZeroEmploymentPeriod = {
  id: string;
  personID: string;
  percentage: number;
  validFrom: number | string;
  validTo?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyPeriod(row: ZeroEmploymentPeriod): EmploymentPeriod {
  return {
    id: row.id,
    person_id: row.personID,
    percentage: row.percentage,
    valid_from: toLegacyDate(row.validFrom) ?? "",
    valid_to: toLegacyDate(row.validTo),
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toZeroPeriodInput(period: EmploymentPeriodInsert) {
  return {
    id: period.id ?? generateUUID(),
    personID: period.person_id,
    percentage: period.percentage,
    validFrom: period.valid_from,
    validTo: period.valid_to ?? null,
  };
}

function toZeroPeriodUpdate(id: string, updates: EmploymentPeriodUpdate) {
  return {
    id,
    ...(updates.person_id !== undefined ? { personID: updates.person_id } : {}),
    ...(updates.percentage !== undefined
      ? { percentage: updates.percentage }
      : {}),
    ...(updates.valid_from !== undefined
      ? { validFrom: updates.valid_from }
      : {}),
    ...(updates.valid_to !== undefined ? { validTo: updates.valid_to } : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useEmploymentPeriods(personId: string | undefined) {
  const query = personId
    ? appQueries.employmentPeriods.byPerson({ personID: personId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const periods = ((data ?? []) as ZeroEmploymentPeriod[]).map(toLegacyPeriod);

  return {
    data: periods,
    isLoading:
      !!personId && details.type !== "complete" && periods.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateEmploymentPeriod() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (period: EmploymentPeriodInsert) => {
    const input = toZeroPeriodInput(period);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.employmentPeriods.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Employment period create was rejected",
      );
      return toLegacyPeriod(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      period: EmploymentPeriodInsert,
      options?: MutationOptions<EmploymentPeriod>,
    ) => {
      void run(period)
        .then((data) => {
          toast.success("Employment period added");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to add employment period: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateEmploymentPeriod() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: EmploymentPeriodUpdate;
  }) => {
    const input = toZeroPeriodUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.employmentPeriods.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Employment period update was rejected",
      );
      return toLegacyPeriod({
        id,
        personID: input.personID ?? "",
        percentage: input.percentage ?? 0,
        validFrom: input.validFrom ?? "",
        validTo: input.validTo ?? null,
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: EmploymentPeriodUpdate },
      options?: MutationOptions<EmploymentPeriod>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Employment period updated");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update employment period: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteEmploymentPeriod() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, personId }: { id: string; personId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.employmentPeriods.delete({ id }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Employment period delete was rejected",
      );
      return { personId };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; personId: string },
      options?: MutationOptions<{ personId: string }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Employment period deleted");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to delete employment period: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
